/**
 * Color Utility Functions
 * 
 * Provides utilities for color conversion, validation, and contrast calculation
 * to support the theming system.
 */

/**
 * Validates if a string is a valid hexadecimal color format
 * @param {string} hex - The color string to validate (e.g., "#16a34a")
 * @returns {boolean} true if the string is a valid hex color, false otherwise
 */
export function isValidHex(hex) {
  return /^#[0-9A-F]{6}$/i.test(hex);
}

/**
 * Converts a hexadecimal color to HSL format for CSS variables
 * @param {string} hex - The hex color string (e.g., "#16a34a")
 * @returns {string} HSL string in format "H S% L%" (e.g., "142 76% 36%")
 */
export function hexToHSL(hex) {
  // Remove the hash if present
  const cleanHex = hex.replace('#', '');
  
  // Parse RGB values
  const r = parseInt(cleanHex.substring(0, 2), 16) / 255;
  const g = parseInt(cleanHex.substring(2, 4), 16) / 255;
  const b = parseInt(cleanHex.substring(4, 6), 16) / 255;

  // Find min and max values
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const delta = max - min;

  // Calculate lightness
  let l = (max + min) / 2;

  // Calculate saturation
  let s = 0;
  if (delta !== 0) {
    s = l > 0.5 ? delta / (2 - max - min) : delta / (max + min);
  }

  // Calculate hue
  let h = 0;
  if (delta !== 0) {
    if (max === r) {
      h = ((g - b) / delta + (g < b ? 6 : 0)) / 6;
    } else if (max === g) {
      h = ((b - r) / delta + 2) / 6;
    } else {
      h = ((r - g) / delta + 4) / 6;
    }
  }

  // Convert to degrees and percentages
  const hDeg = Math.round(h * 360);
  const sPercent = Math.round(s * 100);
  const lPercent = Math.round(l * 100);

  return `${hDeg} ${sPercent}% ${lPercent}%`;
}

/**
 * Calculates the relative luminance of a color
 * Used for determining contrast ratios
 * @param {string} hex - The hex color string
 * @returns {number} The relative luminance value (0-1)
 */
function getLuminance(hex) {
  const cleanHex = hex.replace('#', '');
  
  const r = parseInt(cleanHex.substring(0, 2), 16) / 255;
  const g = parseInt(cleanHex.substring(2, 4), 16) / 255;
  const b = parseInt(cleanHex.substring(4, 6), 16) / 255;

  // Apply gamma correction
  const rLinear = r <= 0.03928 ? r / 12.92 : Math.pow((r + 0.055) / 1.055, 2.4);
  const gLinear = g <= 0.03928 ? g / 12.92 : Math.pow((g + 0.055) / 1.055, 2.4);
  const bLinear = b <= 0.03928 ? b / 12.92 : Math.pow((b + 0.055) / 1.055, 2.4);

  // Calculate relative luminance
  return 0.2126 * rLinear + 0.7152 * gLinear + 0.0722 * bLinear;
}

/**
 * Calculates the contrast ratio between two colors
 * @param {string} color1 - First hex color
 * @param {string} color2 - Second hex color
 * @returns {number} The contrast ratio (1-21)
 */
export function getContrastRatio(color1, color2) {
  const lum1 = getLuminance(color1);
  const lum2 = getLuminance(color2);
  
  const lighter = Math.max(lum1, lum2);
  const darker = Math.min(lum1, lum2);
  
  return (lighter + 0.05) / (darker + 0.05);
}

/**
 * Determines the appropriate contrasting foreground color (white or black)
 * for a given background color to ensure WCAG AA compliance
 * @param {string} backgroundColor - The background hex color
 * @returns {string} "#ffffff" for white or "#000000" for black
 */
export function getContrastColor(backgroundColor) {
  const luminance = getLuminance(backgroundColor);
  
  // Use white text for dark backgrounds, black text for light backgrounds
  // Threshold of 0.5 provides good contrast in most cases
  return luminance > 0.5 ? '#000000' : '#ffffff';
}

/**
 * Checks if two colors meet WCAG AA contrast requirements
 * @param {string} foreground - The foreground hex color
 * @param {string} background - The background hex color
 * @returns {boolean} true if contrast ratio is >= 4.5:1 (WCAG AA standard)
 */
export function meetsWCAGAA(foreground, background) {
  const ratio = getContrastRatio(foreground, background);
  return ratio >= 4.5;
}

/**
 * Applies custom theme colors globally across the entire application
 * This function updates all CSS variables and hardcoded color classes
 * @param {Object} colors - The color object with primary, secondary, tertiary
 */
export function applyGlobalThemeColors(colors) {
  if (!colors || !colors.primary) return;
  
  try {
    const root = document.documentElement;

    // Convert hex to HSL for CSS variables that need HSL format
    const primaryHSL = hexToHSL(colors.primary);
    const secondaryHSL = hexToHSL(colors.secondary);
    const tertiaryHSL = hexToHSL(colors.tertiary);

    // Update all CSS custom properties used throughout the application
    root.style.setProperty('--admin-primary', colors.primary);
    root.style.setProperty('--admin-primary-hover', colors.primary + 'dd');
    root.style.setProperty('--admin-primary-light', colors.primary + '20');
    root.style.setProperty('--admin-secondary', colors.secondary);
    root.style.setProperty('--admin-accent', colors.primary);
    root.style.setProperty('--admin-accent-hover', colors.primary + 'dd');
    
    // Update all color variations to use primary color
    root.style.setProperty('--admin-purple', colors.primary);
    root.style.setProperty('--admin-pink', colors.primary);
    root.style.setProperty('--admin-orange', colors.primary);
    root.style.setProperty('--admin-cyan', colors.primary);
    
    // Update text colors based on theme mode
    const isDark = document.documentElement.classList.contains('dark');
    if (isDark) {
      root.style.setProperty('--admin-gray-900', colors.tertiary);
      root.style.setProperty('--admin-gray-800', colors.tertiary);
      root.style.setProperty('--admin-gray-700', colors.tertiary);
      root.style.setProperty('--admin-gray-600', colors.tertiary + 'cc');
      root.style.setProperty('--admin-gray-500', colors.tertiary + '99');
      root.style.setProperty('--admin-gray-400', colors.tertiary + '66');
      root.style.setProperty('--admin-gray-300', colors.tertiary + '44');
      root.style.setProperty('--admin-gray-200', colors.secondary + 'dd');
      root.style.setProperty('--admin-gray-100', colors.secondary);
      root.style.setProperty('--admin-gray-50', colors.secondary + '88');
    } else {
      root.style.setProperty('--admin-gray-50', colors.secondary);
      root.style.setProperty('--admin-gray-100', colors.secondary + 'dd');
      root.style.setProperty('--admin-gray-200', colors.secondary + 'bb');
      root.style.setProperty('--admin-gray-300', colors.tertiary + '44');
      root.style.setProperty('--admin-gray-400', colors.tertiary + '66');
      root.style.setProperty('--admin-gray-500', colors.tertiary + '88');
      root.style.setProperty('--admin-gray-600', colors.tertiary + 'aa');
      root.style.setProperty('--admin-gray-700', colors.tertiary + 'cc');
      root.style.setProperty('--admin-gray-800', colors.tertiary + 'ee');
      root.style.setProperty('--admin-gray-900', colors.tertiary);
    }
    
    // Update status colors to match primary
    root.style.setProperty('--admin-success', colors.primary);
    root.style.setProperty('--admin-info', colors.primary);
    root.style.setProperty('--admin-warning', colors.primary);
    
    // Update Tailwind CSS variables for better integration
    root.style.setProperty('--primary', primaryHSL);
    root.style.setProperty('--primary-foreground', getContrastColor(colors.primary));
    root.style.setProperty('--secondary', secondaryHSL);
    root.style.setProperty('--secondary-foreground', getContrastColor(colors.secondary));
    root.style.setProperty('--accent', primaryHSL);
    root.style.setProperty('--accent-foreground', getContrastColor(colors.primary));
    
    // Apply dynamic styles for hardcoded color classes
    applyDynamicStyles(colors);
    
    // Apply theme to specific UI components
    applyComponentThemes(colors);
  } catch (error) {
    console.error('Failed to apply global theme colors:', error);
  }
}

/**
 * Creates and applies dynamic CSS styles for hardcoded color classes
 * @param {Object} colors - The color object with primary, secondary, tertiary
 */
function applyDynamicStyles(colors) {
  // Create or update dynamic style element
  const styleId = 'global-dynamic-theme-colors';
  let style = document.getElementById(styleId);
  
  if (!style) {
    style = document.createElement('style');
    style.id = styleId;
    document.head.appendChild(style);
  }
  
  // Generate lighter and darker variations
  const primaryLight = colors.primary + '20';
  const primaryLighter = colors.primary + '10';
  const primaryDark = colors.primary + 'dd';
  const primaryDarker = colors.primary + 'bb';
  
  style.textContent = `
    /* Primary Color Classes - All variations */
    .text-primary, .text-violet-600, .text-purple-600, .text-indigo-600 {
      color: ${colors.primary} !important;
    }
    
    .bg-primary, .bg-violet-600, .bg-purple-600, .bg-indigo-600 {
      background-color: ${colors.primary} !important;
    }
    
    .border-primary, .border-violet-500, .border-purple-500, .border-indigo-500 {
      border-color: ${colors.primary} !important;
    }
    
    /* Hover States */
    .hover\\:bg-violet-700:hover, .hover\\:bg-purple-700:hover, .hover\\:bg-indigo-700:hover {
      background-color: ${primaryDark} !important;
    }
    
    .hover\\:text-violet-700:hover, .hover\\:text-purple-700:hover, .hover\\:text-indigo-700:hover {
      color: ${primaryDark} !important;
    }
    
    .hover\\:border-violet-600:hover, .hover\\:border-purple-600:hover, .hover\\:border-indigo-600:hover {
      border-color: ${colors.primary} !important;
    }
    
    /* Focus States */
    .focus\\:border-primary:focus, .focus\\:border-violet-500:focus, .focus\\:border-purple-500:focus {
      border-color: ${colors.primary} !important;
    }
    
    .focus\\:ring-primary:focus, .focus\\:ring-violet-500:focus, .focus\\:ring-purple-500:focus {
      --tw-ring-color: ${colors.primary} !important;
      box-shadow: 0 0 0 2px ${primaryLight} !important;
    }
    
    /* Button Variants */
    .bg-violet-600, .bg-violet-700, .bg-purple-600, .bg-purple-700, .bg-indigo-600, .bg-indigo-700 {
      background-color: ${colors.primary} !important;
    }
    
    .bg-violet-50, .bg-purple-50, .bg-indigo-50 {
      background-color: ${primaryLighter} !important;
    }
    
    .bg-violet-100, .bg-purple-100, .bg-indigo-100 {
      background-color: ${primaryLight} !important;
    }
    
    /* Text Variants */
    .text-violet-50, .text-purple-50, .text-indigo-50 {
      color: ${primaryLighter} !important;
    }
    
    .text-violet-100, .text-purple-100, .text-indigo-100 {
      color: ${primaryLight} !important;
    }
    
    .text-violet-700, .text-purple-700, .text-indigo-700 {
      color: ${primaryDark} !important;
    }
    
    .text-violet-800, .text-purple-800, .text-indigo-800 {
      color: ${primaryDarker} !important;
    }
    
    /* Border Variants */
    .border-violet-100, .border-purple-100, .border-indigo-100 {
      border-color: ${primaryLight} !important;
    }
    
    .border-violet-200, .border-purple-200, .border-indigo-200 {
      border-color: ${primaryLight} !important;
    }
    
    .border-violet-600, .border-purple-600, .border-indigo-600 {
      border-color: ${colors.primary} !important;
    }
    
    /* Pagination Colors */
    .admin-footer-page-btn.active {
      background: ${colors.primary} !important;
      border-color: ${colors.primary} !important;
    }
    
    .admin-footer-checkbox {
      border-color: ${colors.primary} !important;
    }
    
    .admin-footer-checkbox:checked {
      background: ${colors.primary} !important;
      border-color: ${colors.primary} !important;
    }
    
    .admin-footer-checkbox:focus {
      outline-color: ${primaryLight} !important;
    }
    
    .admin-footer-input:focus {
      border-color: ${colors.primary} !important;
      box-shadow: 0 0 0 2px ${primaryLight} !important;
    }
    
    /* Filter Button Colors */
    .admin-filter-active {
      background: ${colors.primary} !important;
      border-color: ${colors.primary} !important;
    }
    
    .admin-filter-active:hover {
      background: ${primaryDark} !important;
    }
    
    /* Admin specific colors */
    .admin-text-gradient {
      color: ${colors.primary} !important;
    }
    
    /* Search icon colors */
    .admin-search-input-header:focus ~ .admin-search-icon-header-clickable {
      color: ${colors.primary} !important;
    }
    
    .admin-search-icon-header-clickable:hover {
      color: ${colors.primary} !important;
      background: ${primaryLight} !important;
    }
    
    /* Table action buttons */
    .admin-table-action-btn.view {
      background: ${colors.primary} !important;
    }
    
    /* Badge colors */
    .admin-badge.template {
      background: ${primaryLight} !important;
      color: ${colors.primary} !important;
      border-color: ${primaryLight} !important;
    }
    
    /* Card hover effects */
    .admin-card:hover {
      border-color: ${primaryLight} !important;
    }
    
    /* Loading spinner */
    .admin-loading-spinner {
      border-top-color: ${colors.primary} !important;
    }
    
    /* Stat chips */
    .admin-stat-total {
      background: ${primaryLighter} !important;
      color: ${colors.primary} !important;
      border-color: ${primaryLight} !important;
    }
    
    /* Action buttons */
    .admin-action-button {
      background: ${colors.primary} !important;
    }
    
    .admin-action-button:hover {
      background: ${primaryDark} !important;
    }
    
    /* Select component focus */
    [data-state="open"] {
      border-color: ${colors.primary} !important;
    }
    
    /* Dropdown menu items */
    .dropdown-menu-item:hover {
      background: ${primaryLighter} !important;
    }
    
    /* Dialog and Modal accents */
    .dialog-header {
      border-bottom-color: ${primaryLight} !important;
    }
    
    /* Form elements */
    .form-input:focus, .form-select:focus, .form-textarea:focus {
      border-color: ${colors.primary} !important;
      box-shadow: 0 0 0 2px ${primaryLight} !important;
    }
    
    /* Tabs */
    .tabs-trigger[data-state="active"] {
      background: ${colors.primary} !important;
      color: white !important;
    }
    
    /* Progress bars */
    .progress-bar {
      background: ${colors.primary} !important;
    }
    
    /* Switches and toggles */
    .switch[data-state="checked"] {
      background: ${colors.primary} !important;
    }
    
    /* Slider */
    .slider-thumb {
      background: ${colors.primary} !important;
    }
    
    .slider-track {
      background: ${primaryLight} !important;
    }
    
    /* Accordion */
    .accordion-trigger:hover {
      color: ${colors.primary} !important;
    }
    
    /* Navigation */
    .nav-link.active {
      color: ${colors.primary} !important;
      background: ${primaryLighter} !important;
    }
    
    /* Breadcrumb */
    .breadcrumb-link:hover {
      color: ${colors.primary} !important;
    }
    
    /* Alert variants */
    .alert-info {
      background: ${primaryLighter} !important;
      border-color: ${primaryLight} !important;
      color: ${colors.primary} !important;
    }
    
    /* Toast notifications */
    .toast-success {
      background: ${colors.primary} !important;
    }
    
    /* Calendar */
    .calendar-day:hover {
      background: ${primaryLight} !important;
    }
    
    .calendar-day.selected {
      background: ${colors.primary} !important;
      color: white !important;
    }
    
    /* Data table */
    .data-table-row:hover {
      background: ${primaryLighter} !important;
    }
    
    /* Sidebar */
    .sidebar-item.active {
      background: ${primaryLight} !important;
      color: ${colors.primary} !important;
    }
    
    /* Charts and graphs */
    .chart-primary {
      fill: ${colors.primary} !important;
      stroke: ${colors.primary} !important;
    }
    
    /* Status indicators */
    .status-active {
      background: ${colors.primary} !important;
    }
    
    /* File upload */
    .file-upload-area:hover {
      border-color: ${colors.primary} !important;
      background: ${primaryLighter} !important;
    }
    
    /* Code syntax highlighting */
    .code-highlight {
      background: ${primaryLighter} !important;
    }
    
    /* Timeline */
    .timeline-item::before {
      background: ${colors.primary} !important;
    }
    
    /* Rating stars */
    .star.filled {
      color: ${colors.primary} !important;
    }
    
    /* Tooltip */
    .tooltip {
      background: ${colors.primary} !important;
    }
    
    /* Popover */
    .popover-content {
      border-color: ${primaryLight} !important;
    }
    
    /* Command palette */
    .command-item[data-selected="true"] {
      background: ${primaryLight} !important;
      color: ${colors.primary} !important;
    }
    
    /* Sheet/Drawer */
    .sheet-header {
      border-bottom-color: ${primaryLight} !important;
    }
    
    /* Separator */
    .separator {
      background: ${primaryLight} !important;
    }
    
    /* Avatar */
    .avatar-fallback {
      background: ${primaryLight} !important;
      color: ${colors.primary} !important;
    }
    
    /* Badge variants */
    .badge-default {
      background: ${colors.primary} !important;
      color: white !important;
    }
    
    .badge-secondary {
      background: ${primaryLight} !important;
      color: ${colors.primary} !important;
    }
    
    /* Skeleton loader */
    .skeleton {
      background: linear-gradient(90deg, ${primaryLighter} 25%, ${primaryLight} 50%, ${primaryLighter} 75%) !important;
    }
    
    /* Resizable handle */
    .resize-handle:hover {
      background: ${colors.primary} !important;
    }
    
    /* Context menu */
    .context-menu-item:hover {
      background: ${primaryLight} !important;
      color: ${colors.primary} !important;
    }
    
    /* Menubar */
    .menubar-item:hover {
      background: ${primaryLight} !important;
      color: ${colors.primary} !important;
    }
    
    /* Toggle group */
    .toggle-group-item[data-state="on"] {
      background: ${colors.primary} !important;
      color: white !important;
    }
    
    /* Collapsible */
    .collapsible-trigger:hover {
      background: ${primaryLight} !important;
    }
    
    /* Hover card */
    .hover-card-content {
      border-color: ${primaryLight} !important;
    }
  `;
}

/**
 * Applies theme colors to specific UI components that need special handling
 * @param {Object} colors - The color object with primary, secondary, tertiary
 */
function applyComponentThemes(colors) {
  // Update any component-specific styling that can't be handled by CSS variables
  
  // Update chart colors if any chart libraries are present
  if (window.Chart) {
    window.Chart.defaults.color = colors.primary;
  }
  
  // Update any third-party component themes
  const thirdPartyComponents = document.querySelectorAll('[data-theme]');
  thirdPartyComponents.forEach(component => {
    component.style.setProperty('--theme-primary', colors.primary);
    component.style.setProperty('--theme-secondary', colors.secondary);
    component.style.setProperty('--theme-tertiary', colors.tertiary);
  });
  
  // Trigger custom events for components that need to react to theme changes
  const themeChangeEvent = new CustomEvent('themeColorsChanged', {
    detail: { colors }
  });
  document.dispatchEvent(themeChangeEvent);
}